import {html, TemplateResult} from "lit";
import {ReservationFormConfig, ReservationFormData} from "../../../@types/reservation-form.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import '../reservation-badges/reservation-badges.js';
import {Messages} from "../../../@types/online-reservation.js";
import { BdkTextField } from '@breuni-ui/bdk-text-field';
import { BdkCheckbox } from '@breuni-ui/bdk-checkbox';
import { BdkRadioButton } from '@breuni-ui/bdk-radio-button';
import { safeDefine } from '@breuni-ui/safe-define';
import {Step} from "./step.js";

safeDefine('bdk-ents-text-field', BdkTextField);
safeDefine('bdk-ents-checkbox', BdkCheckbox);
safeDefine('bdk-ents-radio-button', BdkRadioButton);

export interface ORFormParams {
  handleInputChange: (event: Event) => void;
  handleRadioChange: (salutation: 'ms' | 'mr') => void;
  validateField: (event: Event) => void;
  validateEmail: (event: Event) => void;
  submitReservation: (event: Event) => void;
}

export const validateEmailValue = (emailValue: string): boolean => {
  const email = emailValue.trim();
  return email === '' || !EMAIL_REGEXP.test(email);
};

export interface StepFourState {
  formData: ReservationFormData;
  fieldErrors: Record<string, boolean>;
}

export const createInitialStepFourState = (): StepFourState => ({
  formData: {
    salutation: 'ms',
    firstName: '',
    lastName: '',
    email: '',
    newsletter: false
  },
  fieldErrors: {
    firstName: false,
    lastName: false,
    email: false
  }
});

export const localPart =
  '(?:[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+' +
  '(?:\\.[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+)*' +
  '|"(?:[\\x20-\\x21\\x23-\\x5b\\x5d-\\x7e]' +
  '|\\\\[\\x20-\\x7e])*")';

export const domain =
  '(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+' +
  '[a-zA-Z]{2,}';

export const ipv4 = '\\[(?:\\d{1,3}\\.){3}\\d{1,3}\\]';

export const EMAIL_REGEXP = new RegExp(`^${localPart}@(?:${domain}|${ipv4})$`);

export class ORForm extends Step {
  private handleInputChange: (event: Event) => void;
  private handleRadioChange: (salutation: 'ms' | 'mr') => void;
  private validateField: (event: Event) => void;
  private validateEmail: (event: Event) => void;
  private submitReservation: (event: Event) => void;

  constructor(
    private messages: Messages,
    private config: ReservationFormConfig,
    private state: StepFourState,
    private showSizeBadge: boolean,
    params: ORFormParams
  ) {
    super();
    this.handleInputChange = params.handleInputChange;
    this.handleRadioChange = params.handleRadioChange;
    this.validateField = params.validateField;
    this.validateEmail = params.validateEmail;
    this.submitReservation = params.submitReservation;
  }

  protected renderContent(): TemplateResult {
    return html`
      <reservation-badges
        .sizeDescription=${this.config.size}
        .storeLocationName=${this.config.storeLocation.name}
        .showSizeBadge=${this.showSizeBadge}
      ></reservation-badges>
      <div>
        ${this.renderSalutationSection()}

        ${this.renderFormField('firstName', this.messages.firstName, this.messages.firstNameError, this.validateField)}
        ${this.renderFormField('lastName', this.messages.lastName, this.messages.lastNameError, this.validateField)}
        ${this.renderFormField('email', this.messages.email, this.messages.emailError, this.validateEmail, 'email')}

        ${this.renderNewsletterCheckbox()}
        ${this.renderPrivacyInfo()}
      </div>
    `;
  }

  protected renderFooter(): TemplateResult {
    return this.renderSubmitButton();
  }

  protected renderTitle(): TemplateResult {
    return html`
      <h2 class="ents-or-reservation-form-title ents-heading-medium" data-test-ents-or-stepfour-header>
        ${this.messages.reservationFormTitle}
      </h2>
    `;
  }

  private renderSalutationSection() {
    return html`
    <div class="ents-or-reservation-form-salutation">
      <bdk-ents-radio-button name="salutation" value="ms" size="medium" id="salutation-ms"
                             ?checked=${this.state.formData.salutation === 'ms'}
                             @change=${() => this.handleRadioChange('ms')}>
        ${this.messages.salutationMs}
      </bdk-ents-radio-button>
      <bdk-ents-radio-button name="salutation" value="mr" size="medium" id="salutation-mr"
                             ?checked=${this.state.formData.salutation === 'mr'}
                             @change=${() => this.handleRadioChange('mr')}>
        ${this.messages.salutationMr}
      </bdk-ents-radio-button>
    </div>
  `;
  }

  private renderFormField(
    fieldName: string,
    label: string,
    errorMessage: string,
    validateField: (event: Event) => void,
    type = "text"
  ) {
    const hasError = this.state.fieldErrors[fieldName];

      return html`
      <bdk-ents-text-field
        class="fs-mask ents-or-reservation-form-field"
        type="${type}"
        id="${fieldName}"
        name="${fieldName}"
        .value=${this.state.formData[fieldName as keyof ReservationFormData]}
        @input=${this.handleInputChange}
        @blur=${validateField}
        required
        maxlength="100"
        aria-required="true"
        ?invalid=${hasError}
        aria-describedby=${hasError ? `${fieldName}Error` : null}>
        ${label}
        <div slot="error" id="${fieldName}Error"  role="alert" aria-live="assertive">
          ${hasError ? errorMessage : ''}
        </div>
      </bdk-ents-text-field>
    `;
  }

  private renderNewsletterCheckbox() {
    return html`
    <bdk-ents-checkbox
      class="ents-or-reservation-form-newsletter"
      id="newsletter"
      name="newsletter"
      multiline
      ?checked=${this.state.formData.newsletter}
      @change=${this.handleInputChange}
      size="small">
        ${unsafeHTML(this.messages.newsletter)}
    </bdk-ents-checkbox>
  `;
  }

  private renderPrivacyInfo() {
    return html`
    <div class="ents-or-reservation-form-privacy ents-copy-extra-small">
      ${this.messages.privacyInfo}
      <a href=${this.config.dataProtectionRegulationLink} target="_blank"
         class="bdk-link-inline">${this.messages.privacyLink}</a>.
    </div>
  `;
  }

  private renderSubmitButton(
  ) {
    return html`
    <button class="bdk-button ents-or-reservation-form-button"
            type="button"
            variant="primary"
            size="medium"
            full-width=""
            @click=${this.submitReservation}>
    ${this.messages.submitButton}
    </button>
  `;
  }
}

export interface StepFourState {
  formData: ReservationFormData;
  fieldErrors: Record<string, boolean>;
}
